// Step checkboxes on the agent draft panel (2026-10-26, docs/contact-view.md,
// "The agent draft panel"). Every step starts checked; a person may uncheck a
// step they don't want processed. The set is recorded on the suggestion's
// call log (model_meta.apiCalls, the same place a step's processed state is
// restored from), one entry per change, the latest wins, so a reload or
// another person opening the contact sees it. At the decision the unchecked
// steps' types are recorded once more ("not processed by choice"), for the
// Audit page's step agreement.

export const STEP_UNCHECKED_TOOLTIP = "Unchecked: this step won't be processed"
// An unchecked step's own Process (or Check) button, on any layout
// (2026-11-04): shown disabled with this tooltip, so the click the server
// would refuse (STEP_UNCHECKED_ERROR) is never sent.
export const STEP_UNCHECKED_PROCESS_TOOLTIP = 'Unchecked: check the step to process it'
export const STEP_UNCHECKED_ERROR = 'step_unchecked'

const isIndex = (n) => Number.isInteger(n) && n >= 0

// The unchecked step indexes recorded on a suggestion's call log: the latest
// step-check entry's, [] when there is none.
export function uncheckedStepsFrom(apiCalls) {
  const list = Array.isArray(apiCalls) ? apiCalls : []
  for (let k = list.length - 1; k >= 0; k--) {
    const c = list[k]
    if (c?.stepChecks === true) {
      return [...new Set((Array.isArray(c.unchecked) ? c.unchecked : []).map((u) => Number(u?.index ?? u)).filter(isIndex))].sort((a, b) => a - b)
    }
  }
  return []
}

const stepLabel = (a, i) => `step ${i + 1}${a?.label ? ` (${a.label})` : ''}`

// The call-log entry for a new unchecked set. No stepIndex: it is not a
// processed step.
export function stepChecksEntry({ unchecked = [], actions = [], actedBy = null, at = new Date().toISOString() } = {}) {
  const indexes = [...new Set((unchecked ?? []).map(Number).filter(isIndex))].sort((a, b) => a - b)
  const label = indexes.length
    ? `Unchecked (won't be processed): ${indexes.map((i) => stepLabel(actions?.[i], i)).join(', ')}`
    : 'Every step checked'
  return {
    service: 'CXHub', method: 'CHOICE', label, actedBy, phase: 'process', at,
    stepChecks: true,
    unchecked: indexes.map((i) => ({ index: i, type: actions?.[i]?.type ?? null })),
  }
}

// The decision's record of the steps not processed by choice: their action
// types (uncheckedSteps), once, when the suggestion is decided with any.
export function uncheckedDecisionEntry({ unchecked = [], actions = [], status = null, actedBy = null, at = new Date().toISOString() } = {}) {
  const indexes = [...new Set((unchecked ?? []).map(Number).filter(isIndex))].sort((a, b) => a - b)
  if (!indexes.length) return null
  return {
    service: 'CXHub', method: 'DECISION', phase: 'approve', at, actedBy,
    label: `Decided (${status ?? 'unknown'}) with steps not processed by choice: ${indexes.map((i) => stepLabel(actions?.[i], i)).join(', ')}`,
    decisionUnchecked: true,
    uncheckedSteps: indexes.map((i) => actions?.[i]?.type ?? null).filter(Boolean),
  }
}

// The types of the steps not processed by choice: the decision's record when
// there is one, else the latest unchecked set.
export function uncheckedStepTypesFrom(apiCalls, actions = []) {
  const list = Array.isArray(apiCalls) ? apiCalls : []
  for (let k = list.length - 1; k >= 0; k--) {
    const c = list[k]
    if (c?.decisionUnchecked === true) return (Array.isArray(c.uncheckedSteps) ? c.uncheckedSteps : []).filter((t) => typeof t === 'string')
  }
  for (let k = list.length - 1; k >= 0; k--) {
    const c = list[k]
    if (c?.stepChecks === true) {
      return (Array.isArray(c.unchecked) ? c.unchecked : []).map((u) => u?.type ?? actions?.[Number(u?.index ?? u)]?.type ?? null).filter((t) => typeof t === 'string')
    }
  }
  return []
}
